import React, {useEffect, useState} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import JobColumns from 'shared/static/JobColumns.json';
import {jobButtonsHandler} from 'hooks';
import {JobStatus, JOB_STATUS_ALL_PROCESSING} from 'shared/JobStatus';
import {JobTableRender} from 'components';
import {formatPrice, jobId} from 'shared/helpers';
import {isDeviceSmall, useCustomMediaQuery} from 'shared/helpers/DeviceSize';
import {merge} from 'lodash';
import {DateTime, DateType, Format} from '@cabinetsbycomputer/datetime';
import {OptionsType} from 'shared/types';

type JobTableProps = {
    title: string;
    jobData: object;
    className: string;
    columns: object;
    link: string;
    mobileRender: boolean;
    tableOrder: number;
    tablesWithValue: number;
    fromApi: boolean;
    api: string;
    order?: {
        fields: number[];
        order: string;
    };
    mobileCardView?: boolean;
    headerAction?: React.ReactNode;
};

interface RowData {
    status: number;
    resetPriceStatus: boolean;
    productCount: number;
    preliminaryCost: number;
    currencyType?: string;
}

export interface JobTableData {
    api: string;
    title: string;
    length: number;
    tableOption: OptionsType;
    link: string;
    mobileRender: boolean;
    className: string;
}

const JobTable = ({
    title,
    jobData,
    className,
    columns,
    link,
    mobileRender,
    tableOrder,
    tablesWithValue,
    fromApi,
    api,
    order,
    mobileCardView = false,
    headerAction,
}: JobTableProps) => {
    const isSearch = className == 'search-jobs';
    const isDisplayJobs = className == 'display-jobs';
    const isSmallDevice = isDeviceSmall();
    const fullWidthTableThreshold = useCustomMediaQuery({maxWidth: 992});
    const {userProfile} = useAppContext();
    const [limit, setLimit] = useState(isSmallDevice ? 10 : 15);

    useEffect(() => {
        setLimit(isSmallDevice ? 10 : 15);
    }, [isSmallDevice]);

    /**
     * Get Job Available Buttons
     */
    const {buttons, dialog, pdfDialog} = jobButtonsHandler(isSearch);

    /**
     * Build Header Columns of Table
     */
    const buildTableHeaders = (columns: Object): Object => {
        const fieldHeader = [];

        columns.forEach((column) => {
            if (
                JobColumns[column].hasOwnProperty('displayProp') &&
                userProfile[JobColumns[column].displayProp] == 0
            ) {
                return;
            }

            fieldHeader.push({
                fieldName: column,
                title: JobColumns[column].title,
                format: (value, row) =>
                    columnFormatter(JobColumns[column].type, value, row),
                alignLeft: JobColumns[column].alignLeft,
            });
        });

        return fieldHeader;
    };

    /**
     * Returns Formatted Value
     * of Column selected
     */
    const columnFormatter = (
        columnType: string,
        value: string | number | boolean,
        row: RowData
    ): string | number | boolean => {
        switch (columnType) {
            case 'id':
                return jobId(value);
            case 'status':
                const jobStatus = userProfile?.jobStatuses?.find(
                    (item) => item.status === value
                );
                if (!!jobStatus && jobStatus?.custom_label) {
                    // status with custom label, others will get from default mapping
                    return jobStatus.custom_label;
                }
                if (typeof JobStatus[value as number] !== 'undefined') {
                    return JobStatus[value as number];
                }
                return '-';
            case 'price':
                if (row?.resetPriceStatus) {
                    return '-';
                }
                if (
                    isDisplayJobs &&
                    userProfile.jobConfirmationEnabled &&
                    JOB_STATUS_ALL_PROCESSING.includes(row.status)
                ) {
                    return (
                        formatPrice(
                            row?.productCount > 0
                                ? Number(row.preliminaryCost)
                                : 0,
                            row
                        ) + ' *'
                    );
                }
                return formatPrice(
                    row?.productCount > 0 ? Number(value) : 0,
                    row
                );
            case 'datetime':
            case 'date':
                try {
                    let date: DateTime;

                    if (
                        value !== null &&
                        typeof value === 'object' &&
                        'date' in value
                    ) {
                        date = DateTime.parse(value.date as string);
                    } else {
                        date = DateTime.parse(value as string, DateType.ISO);
                    }

                    let dateFormatted;
                    if (columnType === 'date') {
                        dateFormatted = date
                            .format(Format.DayMonthYear)
                            .addOrdinalSuffix()
                            .toString();
                    } else {
                        dateFormatted = date
                            .format(Format.DayMonthYearAtTimeFormat)
                            .addOrdinalSuffix()
                            .toString();
                    }

                    const yearMatch = dateFormatted.match(/\b\d{4}\b/);

                    if (yearMatch) {
                        const year = parseInt(yearMatch[0]);

                        // Check if the year is 1970 or less
                        if (year <= 1970) {
                            return '-';
                        }
                    }

                    return dateFormatted;
                } catch (e) {
                    return '-';
                }
            default:
                return value;
        }
    };

    /**
     * Determines width of Table for UI
     */
    const tableWidthSetting = (
        tablesWithValue: number,
        tableOrder: number
    ): number => {
        let tableWidth = 12;

        if (fullWidthTableThreshold) return tableWidth;

        if (tablesWithValue % 2 == 1 && tableOrder > 0) {
            tableWidth = tableOrder % 2 == 0 ? 5 : 7;
        } else if (tablesWithValue % 2 == 0) {
            tableWidth = tableOrder % 2 == 0 ? 7 : 5;
        }

        return tableWidth;
    };

    /**
     * Build Table Options
     */
    let tableOption = {
        fields: buildTableHeaders(columns),
        data: jobData,
        hasCursor: true,
        options: buttons,
        legendContent:
            isDisplayJobs && userProfile.jobConfirmationEnabled
                ? '* Indicates preliminary costs'
                : '',
    } as OptionsType;

    if (fromApi) {
        const apiData = {
            fromApi: fromApi,
            api: api,
            pagination: true,
            limit: limit,
            orderBy: !!order
                ? order
                : {
                      fields: [9],
                      order: 'desc',
                  },
        };

        tableOption = merge(tableOption, apiData);
    }

    /**
     * Job Table Settings
     */
    const jobTableData: JobTableData[] = [
        {
            title: title,
            length: jobData ? jobData.length : 0,
            tableOption: tableOption,
            link: link,
            mobileRender: mobileRender,
            className: className,
        },
    ];

    /**
     * Call Render Component
     */
    return (
        <>
            <JobTableRender
                jobTableData={jobTableData}
                isSearch={isSearch}
                mdValue={tableWidthSetting(tablesWithValue, tableOrder)}
                useMobileCards={mobileCardView}
                headerAction={headerAction}
            />
            {dialog}
            {pdfDialog}
        </>
    );
};

export {JobTable};
